import {
  doc,
  getDoc,
  onSnapshot,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import type {
  AiVerification,
  AiVerificationAnswerValue,
  AiVerificationSnapshot,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * 特定 postId に対する自分の AiVerification doc を subscribe する hook (Sprint V)。
 *
 * Path: `/districts/{districtId}/posts/{postId}/verifications/{uid}`
 * firestore.rules で `verifierUid == auth.uid` の本人 read を許可 (Sprint V で開放)。
 *
 * doc が存在しないとき (未回答) は `verification: null` の ready 状態を返す。
 *
 * R-70 P3b: `selfUids` (現 UID + 端末引き継ぎ元 UID 群) を渡すと、現 UID が未回答でも引き継ぎ元 UID で
 * 回答済みなら、その回答を返す (引き継ぎ後に同じ投稿を再検証してしまう二重回答を防ぐ)。
 * 現 UID の doc は onSnapshot で live 監視 (自端末での編集を即反映)、引き継ぎ元 UID の doc は
 * one-shot getDoc で確認する (別端末作成なので変化しない)。write (submit) は現 UID へ native に行う
 * (docs/98 R-70 方針(a)、残重複は分析側 dedup)。rules は isLinkedOwner で引き継ぎ元 read を許可済。
 */

export type MyVerificationState =
  | { status: 'loading'; verification: null }
  | { status: 'ready'; verification: AiVerification | null };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asAnswerValue(value: unknown): AiVerificationAnswerValue | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  switch (v.type) {
    case 'three_state':
      if (v.value === 'valid' || v.value === 'neutral' || v.value === 'invalid') {
        return { type: 'three_state', value: v.value };
      }
      return null;
    case 'binary':
      if (v.value === 'valid' || v.value === 'invalid') {
        return { type: 'binary', value: v.value };
      }
      return null;
    case 'five_scale':
      if (v.value === 1 || v.value === 2 || v.value === 3 || v.value === 4 || v.value === 5) {
        return { type: 'five_scale', value: v.value };
      }
      return null;
    case 'yes_no':
      if (typeof v.value === 'boolean') {
        return { type: 'yes_no', value: v.value };
      }
      return null;
    case 'comment':
      if (typeof v.value === 'string') {
        return { type: 'comment', value: v.value };
      }
      return null;
    case 'custom_choice':
      if (Array.isArray(v.value)) {
        const values = v.value.filter((x): x is string => typeof x === 'string');
        const out: AiVerificationAnswerValue = { type: 'custom_choice', value: values };
        if (typeof v.other === 'string') out.other = v.other;
        return out;
      }
      return null;
    default:
      return null;
  }
}

function asPerPostAnswers(value: unknown): Record<string, AiVerificationAnswerValue> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, AiVerificationAnswerValue> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const av = asAnswerValue(v);
    if (av) out[k] = av;
  }
  return out;
}

function asPerClassificationAnswers(
  value: unknown,
): Record<string, Record<string, AiVerificationAnswerValue>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, Record<string, AiVerificationAnswerValue>> = {};
  for (const [rank, qs] of Object.entries(value as Record<string, unknown>)) {
    out[rank] = asPerPostAnswers(qs);
  }
  return out;
}

function asAiSnapshot(value: unknown): AiVerificationSnapshot {
  if (!value || typeof value !== 'object') {
    return { analyzedAt: '', settingsVersion: null };
  }
  const v = value as Record<string, unknown>;
  return {
    analyzedAt: typeof v.analyzedAt === 'string' ? v.analyzedAt : '',
    settingsVersion: typeof v.settingsVersion === 'number' ? v.settingsVersion : null,
  };
}

/** Firestore doc → AiVerification (defensive narrow)。 */
export function mapVerification(snap: { id: string; data: () => DocumentData }): AiVerification {
  const data = snap.data();
  return {
    participantUid: typeof data.participantUid === 'string' ? data.participantUid : snap.id,
    assignmentSetId: typeof data.assignmentSetId === 'string' ? data.assignmentSetId : null,
    templateId: typeof data.templateId === 'string' ? data.templateId : '',
    templateVersion: typeof data.templateVersion === 'number' ? data.templateVersion : 0,
    aiSnapshot: asAiSnapshot(data.aiSnapshot),
    perPostAnswers: asPerPostAnswers(data.perPostAnswers),
    perClassificationAnswers: asPerClassificationAnswers(data.perClassificationAnswers),
    freeComment: typeof data.freeComment === 'string' ? data.freeComment : null,
    completed: typeof data.completed === 'boolean' ? data.completed : false,
    submittedAt: tsToIso(data.submittedAt),
    updatedAt: tsToIso(data.updatedAt),
  };
}

export function useMyVerification(
  districtId: string,
  postId: string | null,
  uid: string | null,
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群。省略時は uid 単独 (後方互換)。 */
  selfUids?: string[],
): MyVerificationState {
  const [state, setState] = useState<MyVerificationState>({
    status: 'loading',
    verification: null,
  });
  // 引き継ぎ元 UID (現 UID を除いた self UID 群)。これらは別端末作成のため one-shot read。
  const linkedUids = (selfUids ?? []).filter((u) => u && u !== uid);
  const linkedKey = linkedUids.join('|');
  // onSnapshot 発火ごとに採番し、引き継ぎ元 one-shot の async が後続発火 (現 UID 回答の新規作成等) に
  // 追い越された場合に stale 結果で上書きしないためのガード。
  const runRef = useRef(0);

  useEffect(() => {
    if (!postId || !uid) {
      setState({ status: 'loading', verification: null });
      return;
    }
    let cancelled = false;
    const ref = doc(db, 'districts', districtId, 'posts', postId, 'verifications', uid);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const myRun = ++runRef.current;
        if (snap.exists()) {
          if (!cancelled) setState({ status: 'ready', verification: mapVerification(snap) });
          return;
        }
        // 現 UID 未回答 → 引き継ぎ元 UID を one-shot 確認 (引き継ぎ後の二重回答防止)。
        if (linkedUids.length === 0) {
          if (!cancelled) setState({ status: 'ready', verification: null });
          return;
        }
        void (async () => {
          let found: AiVerification | null = null;
          for (const lu of linkedUids) {
            try {
              const ls = await getDoc(
                doc(db, 'districts', districtId, 'posts', postId, 'verifications', lu),
              );
              if (ls.exists()) {
                found = mapVerification(ls);
                break;
              }
            } catch {
              // claim 上限超で read 拒否されても無視 (その UID 分は未検出に縮退)。
            }
          }
          // 後続の onSnapshot 発火に追い越されていたら破棄 (現 UID の回答が優先)。
          if (!cancelled && myRun === runRef.current) {
            setState({ status: 'ready', verification: found });
          }
        })();
      },
      () => {
        // permission-denied 等で stale なときも null として ready 扱い (UI 側で defensive)
        if (!cancelled) setState({ status: 'ready', verification: null });
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
    // linkedUids は配列参照が毎回変わるため、安定な linkedKey (join) を dependency にする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, postId, uid, linkedKey]);

  return state;
}
